// C:\Users\abdul\Downloads\Onebox\onebox\src\components\Menu.jsx
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import './Menu.css';
import smallLeaf       from '../assets/images/small-leaf.png';
import veganIcon       from '../assets/images/dietary/vegan.png';
import pescatarianIcon from '../assets/images/dietary/Pescatarian.png';
import halalIcon       from '../assets/images/dietary/Halal.png';
import vegIcon         from '../assets/images/dietary/veg.png';
import nonVegIcon      from '../assets/images/dietary/Non-veg.png';

const DIETARY_ICONS = {
  vegan:          { icon: veganIcon,        label: 'Vegan' },
  vegetarian:     { icon: vegIcon,          label: 'Veg' },
  pescatarian:    { icon: pescatarianIcon,  label: 'Pescatarian' },
  halal:          { icon: halalIcon,        label: 'Halal' },
  non_vegetarian: { icon: nonVegIcon,       label: 'Non-Veg' },
};

const Menu = ({ tenant = {}, onAddToCart }) => {
  const [activeTab, setActiveTab]         = useState('');
  const [categories, setCategories]       = useState([]);
  const [menuItems, setMenuItems]         = useState([]);
  const [menuData, setMenuData]           = useState(null);
  const [loading, setLoading]             = useState(true);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [addingItemId, setAddingItemId]   = useState(null);
  const [addedItemId, setAddedItemId]     = useState(null);
  const [visibleItems, setVisibleItems]   = useState(new Set());
  const itemRefs = useRef({});

  useEffect(() => {
    if (!tenant?.id) { setLoading(false); return; }
    fetchMenuData();
    fetchCategoriesAndItems();

    const sectionChannel = supabase
      .channel('menu-section-changes')
      .on('postgres_changes', {
        event: '*', schema: 'public',
        table: 'website_sections',
        filter: `tenant_id=eq.${tenant.id}`,
      }, (payload) => {
        if (payload.new?.section_name === 'menu') setMenuData(payload.new.content);
      }).subscribe();

    const itemsChannel = supabase
      .channel('menu-items-changes')
      .on('postgres_changes', {
        event: '*', schema: 'public',
        table: 'menu_items',
        filter: `tenant_id=eq.${tenant.id}`,
      }, () => fetchCategoriesAndItems()).subscribe();

    const categoriesChannel = supabase
      .channel('menu-categories-changes')
      .on('postgres_changes', {
        event: '*', schema: 'public',
        table: 'menu_categories',
        filter: `tenant_id=eq.${tenant.id}`,
      }, () => fetchCategoriesAndItems()).subscribe();

    return () => {
      supabase.removeChannel(sectionChannel);
      supabase.removeChannel(itemsChannel);
      supabase.removeChannel(categoriesChannel);
    };
  }, [tenant?.id]);

  // IntersectionObserver for staggered card entrance
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setVisibleItems(prev => new Set([...prev, entry.target.dataset.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    Object.values(itemRefs.current).forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, [menuItems, activeTab]);

  const fetchMenuData = async () => {
    try {
      const { data, error } = await supabase
        .from('website_sections')
        .select('content, is_visible')
        .eq('tenant_id', tenant.id)
        .eq('section_name', 'menu')
        .maybeSingle();

      if (error || !data?.is_visible) setMenuData(getDefaultData());
      else setMenuData(data.content);
    } catch { setMenuData(getDefaultData()); }
  };

  const fetchCategoriesAndItems = async () => {
    try {
      setLoading(true);

      const { data: categoriesData, error: catError } = await supabase
        .from('menu_categories')
        .select('id, name, slug, icon_name, display_order')
        .eq('tenant_id', tenant.id)
        .eq('is_active', true)
        .is('deleted_at', null)
        .order('display_order', { ascending: true });
      if (catError) throw catError;

      const { data: itemsData, error: itemsError } = await supabase
        .from('menu_items')
        .select('*')
        .eq('tenant_id', tenant.id)
        .eq('is_featured', true)
        .eq('is_available', true)
        .is('deleted_at', null)
        .order('display_order', { ascending: true });
      if (itemsError) throw itemsError;

      const categoriesWithItems = (categoriesData || []).filter(cat =>
        (itemsData || []).some(item => item.category_id === cat.id)
      );

      setCategories(categoriesWithItems);
      setMenuItems(itemsData || []);

      if (categoriesWithItems.length > 0 && !activeTab) {
        setActiveTab(categoriesWithItems[0].slug);
        const firstItem = (itemsData || []).find(
          item => item.category_id === categoriesWithItems[0].id
        );
        if (firstItem?.image_url) setFeaturedImage(firstItem.image_url);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultData = () => ({
    heading: 'From Our Menu',
    subheading: 'Choose & pick',
    is_active: true,
  });

  const primaryColor = tenant?.primary_color || '#FF9F0D';
  const currency     = tenant?.currency || 'INR';

  const formatPrice = (price) => {
    if (currency === 'INR') return `₹${price}`;
    if (currency === 'USD') return `$${price}`;
    if (currency === 'EUR') return `€${price}`;
    return `${currency} ${price}`;
  };

  const calculateDiscount = (originalPrice, currentPrice) => {
    if (!originalPrice || originalPrice <= currentPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
  };

  const activeItems = menuItems.filter(item => {
    if (!activeTab) return true;
    const cat = categories.find(c => c.slug === activeTab);
    return cat && item.category_id === cat.id;
  });

  const handleItemClick = (item) => {
    if (item.image_url) setFeaturedImage(item.image_url);
  };

  // ✅ No auto-open cart — only update badge count
  const handleAddToCart = async (e, item) => {
    e.stopPropagation();
    if (!onAddToCart || addingItemId === item.id) return;

    setAddingItemId(item.id);
    await onAddToCart(item, 1);
    setAddingItemId(null);
    setAddedItemId(item.id);

    setTimeout(() => setAddedItemId(null), 1500);
    // ✅ Cart does NOT auto-open
  };

  if (loading) {
    return (
      <section className="menu-section">
        <div className="menu-container">
          <div className="menu-loading">
            <div className="menu-spinner" style={{ borderTopColor: primaryColor }}></div>
            <p>Loading menu...</p>
          </div>
        </div>
      </section>
    );
  }

  const data = menuData || getDefaultData();
  if (!data?.is_active || categories.length === 0) return null;

  return (
    <section id="menu" className="menu-section">
      <div className="menu-leaf menu-leaf-left">
        <img src={smallLeaf} alt="" className="leaf-img" />
      </div>

      <div className="menu-container">
        {/* Header */}
        <div className="menu-header">
          <span className="menu-label" style={{ color: primaryColor }}>
            {data.subheading || 'Choose & pick'}
          </span>
          <h2 className="menu-title">
            <span className="menu-from" style={{ color: primaryColor }}>
              {(data.heading || 'From Our Menu').split(' ')[0]}
            </span>{' '}
            {(data.heading || 'From Our Menu').split(' ').slice(1).join(' ')}
          </h2>
        </div>

        {/* Category Tabs */}
        <div className="menu-tabs">
          {categories.map((category) => (
            <button
              key={category.id}
              className={`menu-tab ${activeTab === category.slug ? 'active' : ''}`}
              onClick={() => setActiveTab(category.slug)}
              style={{
                borderColor: activeTab === category.slug
                  ? primaryColor : 'rgba(255, 255, 255, 0.15)',
                backgroundColor: activeTab === category.slug
                  ? primaryColor : 'transparent',
              }}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Menu Content */}
        <div className="menu-content">
          {/* Left — Featured Image */}
          {featuredImage && (
            <div className="menu-featured">
              <div className="featured-image-wrapper">
                <img
                  src={featuredImage}
                  alt="Featured dish"
                  className="featured-image"
                  key={featuredImage}
                />
              </div>
            </div>
          )}

          {/* Right — Items Grid */}
          <div className="menu-items">
            {activeItems.length > 0 ? (
              activeItems.map((item, index) => {
                const discount  = calculateDiscount(item.original_price, item.price);
                const isAdding  = addingItemId === item.id;
                const isAdded   = addedItemId  === item.id;
                const isVisible = visibleItems.has(item.id);
                const dietary   = DIETARY_ICONS[item.dietary_type] || DIETARY_ICONS['non_vegetarian'];

                return (
                  <div
                    key={item.id}
                    ref={el => itemRefs.current[item.id] = el}
                    data-id={item.id}
                    className={`menu-item ${isVisible ? 'menu-item-visible' : ''}`}
                    style={{ transitionDelay: `${index * 60}ms` }}
                    onClick={() => handleItemClick(item)}
                  >
                    {/* Image — badges only */}
                    {item.image_url && (
                      <div className="menu-item-image">
                        <img src={item.image_url} alt={item.name} className="item-img" />
                        <div className="item-badges">
                          {item.is_bestseller && (
                            <span className="item-badge badge-bestseller">🔥 Best</span>
                          )}
                          {item.is_new && (
                            <span className="item-badge badge-new">✨ New</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="menu-item-content">
                      {/* Name + dietary icon */}
                      <div className="item-name-row">
                        <h3 className="item-name">{item.name}</h3>
                        <div className="dietary-icon-wrapper" title={dietary.label}>
                          <img
                            src={dietary.icon}
                            alt={dietary.label}
                            className="dietary-icon-img"
                          />
                        </div>
                      </div>

                      {item.description && (
                        <p className="item-description">{item.description}</p>
                      )}

                      {item.preparation_time && (
                        <span className="item-prep-time">
                          ⏱ {item.preparation_time} min
                        </span>
                      )}

                      {/* Price + Add button */}
                      <div className="item-footer">
                        <div className="item-price-group">
                          <span className="item-price" style={{ color: primaryColor }}>
                            {formatPrice(item.price)}
                          </span>
                          {item.original_price && discount > 0 && (
                            <>
                              <span className="item-original-price">
                                {formatPrice(item.original_price)}
                              </span>
                              <span className="item-discount-badge">
                                {discount}% OFF
                              </span>
                            </>
                          )}
                        </div>

                        {onAddToCart && (
                          <button
                            className={`item-add-btn ${isAdding ? 'btn-adding' : ''} ${isAdded ? 'btn-added' : ''}`}
                            style={{
                              backgroundColor: isAdded
                                ? '#10b981'
                                : isAdding
                                ? `${primaryColor}80`
                                : primaryColor,
                              boxShadow: isAdded
                                ? '0 4px 14px rgba(16,185,129,0.5)'
                                : `0 4px 14px ${primaryColor}50`,
                            }}
                            onClick={(e) => handleAddToCart(e, item)}
                            disabled={isAdding}
                            aria-label={`Add ${item.name} to cart`}
                          >
                            {isAdding ? (
                              <span className="add-btn-spinner" />
                            ) : isAdded ? (
                              <span className="add-btn-check">✓</span>
                            ) : (
                              <>
                                <span className="add-btn-icon">+</span>
                                <span className="add-btn-text">Add</span>
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="menu-empty">
                <p>No items available in this category yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Menu;
